import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useModules } from "../contexts/ModulesContext";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertTriangle, Database, Sparkles, Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = {
  MODE: { label: "Mode de fonctionnement", items: ["multi_record"] },
  GEOGRAPHY: { label: "Géographie", items: ["pays", "regions", "sites"] },
  ORGANISATION: { label: "Organisation", items: ["societes", "directions", "services", "equipes"] },
  HR_EXTENDED: { label: "RH étendu", items: ["profiles"] },
};

const MODULE_HINTS = {
  multi_record: "Autorise plusieurs fiches RH simultanées par utilisateur (multi-contrat, multi-affectation).",
};

export default function Settings() {
  const { t } = useI18n();
  const { modules, toggle } = useModules();
  const qc = useQueryClient();
  const [count, setCount] = useState([100]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resetOpen, setResetOpen] = useState(false);

  const getMod = (key) => modules.find((m) => m.module_key === key);

  const handleToggle = async (key, enabled) => {
    try {
      const cascaded = await toggle(key, enabled);
      if (enabled) {
        if (cascaded.length > 0) {
          toast.success(`Module activé (parents activés en cascade : ${cascaded.join(", ")})`);
        } else {
          toast.success("Module activé");
        }
      } else if (cascaded.length > 0) {
        toast.success(`Module désactivé (dépendances désactivées : ${cascaded.join(", ")})`);
      } else {
        toast.success("Module désactivé");
      }
    } catch {
      toast.error("Erreur");
    }
  };

  const generate = async () => {
    setBusy(true);
    setProgress(10);
    const fakeTimer = setInterval(() => setProgress((p) => Math.min(90, p + 5)), 400);
    try {
      const { data } = await api.post("/seed", { count: count[0] });
      clearInterval(fakeTimer);
      setProgress(100);
      toast.success(`${data.data.users} utilisateur(s) générés`);
      qc.invalidateQueries();
    } catch (e) {
      clearInterval(fakeTimer);
      toast.error("Erreur de génération");
    } finally {
      setTimeout(() => { setBusy(false); setProgress(0); }, 500);
    }
  };

  const doReset = async () => {
    setResetOpen(false);
    try {
      await api.post("/reset");
      toast.success("Base réinitialisée");
      qc.invalidateQueries();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="p-8 max-w-[1200px] mx-auto" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="font-heading text-4xl font-bold tracking-tight text-warm-dark">{t("settings")}</h1>
        <p className="text-sm text-warm-dark/60 mt-1">Configuration du lab et génération de données</p>
      </div>

      <div className="space-y-5">
        {/* Modules */}
        <Card className="border-sand shadow-soft p-6" data-testid="modules-card">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} className="text-warm-dark/60" />
            <div className="font-heading text-lg font-semibold text-warm-dark">{t("module_config")}</div>
          </div>
          <p className="text-sm text-warm-dark/60 mb-5">Désactiver un parent désactive automatiquement ses enfants. Les données sont conservées, uniquement masquées.</p>

          <div className="space-y-6">
            {Object.entries(CATEGORIES).map(([cat, { label, items }]) => (
              <div key={cat}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-warm-dark/60 mb-2">{label}</div>
                <div className="space-y-1">
                  {items.map((key) => {
                    const m = getMod(key);
                    if (!m) return null;
                    const deps = m.dependencies || [];
                    return (
                      <div key={key} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-cream" style={{ paddingLeft: deps.length * 20 + 12 }} data-testid={`module-row-${key}`}>
                        <div className="flex items-center gap-2">
                          {deps.length > 0 && <span className="text-warm-dark/30 text-xs">└</span>}
                          <div>
                            <div className="text-sm font-medium text-warm-dark">{m.label}</div>
                            {MODULE_HINTS[key] && (
                              <div className="text-[11px] text-warm-dark/60 max-w-xl">{MODULE_HINTS[key]}</div>
                            )}
                            {deps.length > 0 && (
                              <div className="text-[11px] text-warm-dark/50 mono">{t("dependencies")}: {deps.join(", ")}</div>
                            )}
                          </div>
                        </div>
                        <Switch
                          checked={m.is_enabled}
                          onCheckedChange={(v) => handleToggle(key, v)}
                          data-testid={`module-switch-${key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Seed */}
        <Card className="border-sand shadow-soft p-6" data-testid="seed-card">
          <div className="flex items-center gap-2 mb-1">
            <Database size={16} className="text-warm-dark/60" />
            <div className="font-heading text-lg font-semibold text-warm-dark">{t("demo_generator")}</div>
          </div>
          <p className="text-sm text-warm-dark/60 mb-5">Génère des employés français réalistes avec enregistrements et profils étendus.</p>

          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-warm-dark/80">{t("users_volume")}</div>
                <div className="font-heading text-2xl font-bold text-warm-dark mono">{count[0]}</div>
              </div>
              <Slider value={count} onValueChange={setCount} min={10} max={5000} step={10} className="my-2" data-testid="seed-slider" />
              <div className="flex items-center justify-between text-xs text-warm-dark/50 mono">
                <span>10</span><span>5000</span>
              </div>
              <div className="flex gap-1.5 mt-3 flex-wrap">
                {[10, 50, 100, 500, 1000, 5000].map((n) => (
                  <button
                    key={n}
                    onClick={() => setCount([n])}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${count[0] === n ? "bg-terracotta text-cream border-terracotta-dark" : "bg-white border-sand text-warm-dark/80 hover:bg-cream"}`}
                    data-testid={`seed-preset-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            {busy && <Progress value={progress} className="h-2" />}
            <Button onClick={generate} disabled={busy} className="w-full bg-terracotta hover:bg-terracotta-dark" data-testid="btn-generate">
              {busy ? `Génération… ${progress}%` : t("generate")}
            </Button>
          </div>
        </Card>

        {/* Danger */}
        <Card className="border-red-200 shadow-soft p-6" data-testid="danger-card">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="text-red-600" />
            <div className="font-heading text-lg font-semibold text-red-700">Zone dangereuse</div>
          </div>
          <p className="text-sm text-warm-dark/60 mb-4">Réinitialise toutes les données (utilisateurs, référentiels, affectations) et restaure la config des modules par défaut.</p>
          <Button variant="destructive" onClick={() => setResetOpen(true)} data-testid="btn-reset-db">{t("reset_db")}</Button>
        </Card>

        {/* Diagnostic */}
        <HealthCard />
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle>{t("confirm_reset")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-warm-dark/70">Cette action est irréversible. Toutes les données du SIRH Lab seront effacées.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>{t("cancel")}</Button>
            <Button variant="destructive" onClick={doReset} data-testid="btn-reset-confirm">{t("confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HealthCard() {
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["health"],
    queryFn: async () => (await api.get("/health")).data.data,
    refetchInterval: 30000,
    refetchIntervalInBackground: false, // pause polling when tab is hidden — saves resources
    refetchOnWindowFocus: true,         // refresh when user returns to the tab
  });
  const corsLabel = {
    wildcard: "Wildcard (`*`) — tous domaines acceptés",
    "trusted-zones": "Zones de confiance (regex Maisonéa/Ariovis/Emergent)",
    custom: "Liste custom (CORS_ORIGINS env)",
  };
  return (
    <Card className="border-sand shadow-soft p-6" data-testid="health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-warm-dark/60" />
          <div className="font-heading text-lg font-semibold text-warm-dark">Diagnostic</div>
          {data && (
            <span
              className={`ml-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${data.ok ? "bg-sage-light text-sage" : "bg-error/10 text-error"}`}
              data-testid="health-status-pill"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${data.ok ? "bg-sage" : "bg-error"} ${data.ok ? "animate-pulse" : ""}`} />
              {data.ok ? "Opérationnel" : "Indisponible"}
            </span>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} data-testid="btn-refresh-health">
          <RefreshCw size={13} className={`mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
          Actualiser
        </Button>
      </div>
      <p className="text-sm text-warm-dark/60 mb-4">État de l'API et configuration de déploiement courante. Utile pour diagnostiquer un blocage NIM en démo client.</p>
      {!data && <div className="text-sm text-warm-dark/40">Chargement…</div>}
      {data && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]" data-testid="health-grid">
          <Row k="Version" v={<span className="font-mono">{data.version}</span>} />
          <Row k="Latence DB ping" v={<span className="font-mono">{data.uptime_check_ms}ms</span>} />
          <Row k="Mode CORS" v={corsLabel[data.cors.mode] || data.cors.mode} />
          <Row k="FRONTEND_URL" v={<span className="font-mono break-all">{data.cors.frontend_url}</span>} />
          <Row k="Cookies" v={`${data.cookies.samesite} / Secure=${data.cookies.secure ? "true" : "false"}`} />
          <Row k="DB connectée" v={data.db.ok ? `${data.db.name} ✓` : `❌ ${data.db.error}`} />
          <Row k="Collaborateurs" v={data.db.counts.users} />
          <Row k="Fiches RH" v={data.db.counts.user_records} />
          <Row k="Sites" v={data.db.counts.sites} />
          <Row k="Modules configurés" v={data.db.counts.module_config} />
        </div>
      )}
    </Card>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-sand/60 last:border-b-0">
      <div className="text-warm-dark/60 min-w-[140px] text-[12px]">{k}</div>
      <div className="text-warm-dark font-medium">{v}</div>
    </div>
  );
}
